// useTemplateSettings.ts
//
// Loads all template_settings columns on mount.
// Nothing auto-saves: tweaks + creator-data stay in session until Publish /
// explicit saveNow (tour complete, etc.).
//
// Column → CreatorData field mapping mirrors template_inputs.csv:
//
//  tweaks      → tweaks (Tweaks object: palette/fonts/axes/pages/contentEdits)
//  bio         → creatorData.bio
//  highlights  → creatorData.highlights
//  links       → creatorData.links
//  banners     → creatorData.banners
//  carousels   → creatorData.carousels
//  products    → creatorData.products
//  journal     → creatorData.journal
//  press       → creatorData.press
//  about       → creatorData.about
//  recent      → creatorData.recent

import { useRef, useState, useCallback, useEffect } from 'react';
import {
  loadTemplateSettings,
  saveTemplateSettings,
  uploadTemplateImage,
  updateUserBrowsableTemplateActivityAt,
  type TemplateUpsert,
} from '../../../Utils/SupabaseServices/TemplateDatabase';
import type { Tweaks, CreatorData } from './types';
import { finalizePageSlugContentEdits } from './customWebsitePages';
import {
  FALLBACK_TEMPLATE_TWEAKS,
  mapTemplateSettingsRow,
} from './templateSettingsMap';

// ── Public interface ───────────────────────────────────────────
export interface UseTemplateSettingsResult {
  loading:     boolean;
  saving:      boolean;
  saveStatus:  'saved' | 'error' | null;
  tweaks:      Tweaks;
  creatorData: Partial<CreatorData>;
  setTweak:       (key: keyof Tweaks, value: Tweaks[keyof Tweaks]) => void;
  /** Latest in-memory tweaks (includes writes not yet flushed to React state). */
  getLatestTweaks: () => Tweaks;
  setCreatorField:<K extends keyof CreatorData>(field: K, value: CreatorData[K]) => void;
  uploadImage: (file: File, slot: string) => Promise<string | null>;
  /** Persist current session tweaks + creator data immediately (Publish / tour). */
  saveNow: () => Promise<boolean>;
  /**
   * Rewrite tweaks just before persist so practice-mode fields can stay
   * on screen without being written to the database.
   */
  setSanitizeTweaksForPersist: (
    fn: ((tweaks: Tweaks) => Tweaks) | null,
  ) => void;
  /**
   * Merge a tweaks patch into the in-memory row synchronously, then persist now.
   * Avoids the setState→saveNow race where Publish flags never reach the DB.
   */
  applyTweaksAndSaveNow: (patch: Partial<Tweaks>) => Promise<boolean>;
  /** Replace full tweaks in session only (e.g. discard unpublished edits). */
  replaceTweaks: (next: Tweaks) => void;
  /** Replace creator-data in session only (e.g. discard unpublished edits). */
  replaceCreatorData: (next: Partial<CreatorData>) => void;
  /** Replace full tweaks and persist (rare — prefer replaceTweaks + Publish). */
  replaceTweaksAndSaveNow: (next: Tweaks) => Promise<boolean>;
}

/** When true, settings load from DB but writes are disabled — for public template routes. */
export interface UseTemplateSettingsOptions {
  readOnly?: boolean;
  /**
   * Parent already loaded settings (e.g. `MasterTemplateContentProvider` on `/website`).
   * Skips the initial fetch and starts with `loading: false`.
   */
  preloaded?: {
    tweaks: Tweaks;
    creatorData: Partial<CreatorData>;
  };
}

export function useTemplateSettings(
  userId: string | undefined,
  options?: UseTemplateSettingsOptions,
): UseTemplateSettingsResult {
  const readOnly = Boolean(options?.readOnly);
  const preloaded = options?.preloaded;
  const skipFetch = Boolean(preloaded && userId?.trim());

  const [loading,    setLoading]    = useState(() => (skipFetch ? false : true));
  const [saving,     setSaving]     = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'error' | null>(null);
  const [tweaks,     setTweaksState]     = useState<Tweaks>(
    () => preloaded?.tweaks ?? FALLBACK_TEMPLATE_TWEAKS,
  );
  const [creatorData,setCreatorDataState]= useState<Partial<CreatorData>>(
    () => preloaded?.creatorData ?? {},
  );

  const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestTweaks   = useRef<Tweaks>(preloaded?.tweaks ?? FALLBACK_TEMPLATE_TWEAKS);
  const latestCreator  = useRef<Partial<CreatorData>>(preloaded?.creatorData ?? {});
  const settingsLoadedForUserRef = useRef<string | null>(
    skipFetch && userId?.trim() ? userId.trim() : null,
  );
  /** Always the creator being edited — avoids admin username switches saving to the wrong row. */
  const saveUserIdRef  = useRef<string | undefined>(userId);
  saveUserIdRef.current = userId;
  const sanitizeTweaksForPersistRef = useRef<((tweaks: Tweaks) => Tweaks) | null>(
    null,
  );
  const creatorPersistDirtyRef = useRef(false);

  // ── Load ─────────────────────────────────────────────────────
  useEffect(() => {
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    setSaveStatus(null);
    setSaving(false);

    if (!userId?.trim()) {
      latestTweaks.current = FALLBACK_TEMPLATE_TWEAKS;
      latestCreator.current = {};
      creatorPersistDirtyRef.current = false;
      setTweaksState(FALLBACK_TEMPLATE_TWEAKS);
      setCreatorDataState({});
      settingsLoadedForUserRef.current = null;
      setLoading(false);
      return;
    }

    if (skipFetch && preloaded) {
      latestTweaks.current = preloaded.tweaks;
      latestCreator.current = preloaded.creatorData;
      creatorPersistDirtyRef.current = false;
      setTweaksState(preloaded.tweaks);
      setCreatorDataState(preloaded.creatorData);
      settingsLoadedForUserRef.current = userId.trim();
      setLoading(false);
      return;
    }

    latestTweaks.current = FALLBACK_TEMPLATE_TWEAKS;
    latestCreator.current = {};
    creatorPersistDirtyRef.current = false;
    setTweaksState(FALLBACK_TEMPLATE_TWEAKS);
    setCreatorDataState({});
    setLoading(true);

    let cancelled = false;

    void (async () => {
      try {
        const row = await loadTemplateSettings(userId);
        if (cancelled) return;
        if (row) {
          const { tweaks: t, creatorData: c } = mapTemplateSettingsRow(row);
          latestTweaks.current = t;
          latestCreator.current = c;
          creatorPersistDirtyRef.current = false;
          setTweaksState(t);
          setCreatorDataState(c);
        }
        settingsLoadedForUserRef.current = userId.trim();
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('[useTemplateSettings] load failed:', err);
        // Unblock the UI with fallback defaults so the editor doesn't hang forever.
        settingsLoadedForUserRef.current = userId.trim();
        setLoading(false);
        setSaveStatus('error');
        statusTimerRef.current = setTimeout(() => setSaveStatus(null), 4000);
      }
    })();

    return () => {
      cancelled = true;
    };
    // `preloaded` is only read when `skipFetch` flips on for this `userId` (public `/website` mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, skipFetch]);

  // ── Persist (explicit only — Publish / saveNow / tour) ────────
  const persistNow = useCallback(async (): Promise<boolean> => {
    const uid = saveUserIdRef.current?.trim();
    if (!uid || readOnly) return false;

    setSaving(true);

    try {
      const c = latestCreator.current;
      // Finalize nav Link slugs before any tour sanitize / DB write so a
      // trailing `-` left while typing cannot 404 the public site.
      const withFinalSlugs = {
        ...latestTweaks.current,
        contentEdits: finalizePageSlugContentEdits(
          latestTweaks.current.contentEdits,
        ),
      };
      latestTweaks.current = withFinalSlugs;
      setTweaksState(withFinalSlugs);
      const tweaksToSave = sanitizeTweaksForPersistRef.current
        ? sanitizeTweaksForPersistRef.current(withFinalSlugs)
        : withFinalSlugs;
      const fullPayload: TemplateUpsert = {
        tweaks:     tweaksToSave as unknown as Record<string, unknown>,
        bio:        (c.bio       ?? {}) as Record<string, unknown>,
        highlights: (c.highlights ?? []) as unknown[],
        links:      (c.links      ?? []) as unknown[],
        banners:    (c.banners    ?? {}) as Record<string, unknown>,
        carousels:  (c.carousels  ?? []) as unknown[],
        products:   (c.products   ?? []) as unknown[],
        press:      (c.press      ?? []) as unknown[],
        about:      (c.about      ?? {}) as Record<string, unknown>,
        recent:     (c.recent     ?? []) as unknown[],
      };
      const payload: TemplateUpsert = creatorPersistDirtyRef.current
        ? fullPayload
        : { tweaks: fullPayload.tweaks };

      const result = await saveTemplateSettings(uid, payload, fullPayload);
      if (saveUserIdRef.current?.trim() !== uid) return result.ok;
      if (result.ok) creatorPersistDirtyRef.current = false;

      const status = result.ok ? 'saved' : 'error';
      setSaveStatus(status);
      if (result.ok) {
        void updateUserBrowsableTemplateActivityAt(uid);
      }
      if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
      statusTimerRef.current = setTimeout(() => setSaveStatus(null), 3000);
      return result.ok;
    } catch {
      setSaveStatus('error');
      return false;
    } finally {
      setSaving(false);
    }
  }, [readOnly]);

  const saveNow = useCallback(async () => persistNow(), [persistNow]);

  const setSanitizeTweaksForPersist = useCallback(
    (fn: ((tweaks: Tweaks) => Tweaks) | null) => {
      sanitizeTweaksForPersistRef.current = fn;
    },
    [],
  );

  const applyTweaksAndSaveNow = useCallback(
    async (patch: Partial<Tweaks>) => {
      const next = { ...latestTweaks.current, ...patch };
      latestTweaks.current = next;
      setTweaksState(next);
      return persistNow();
    },
    [persistNow],
  );

  const replaceTweaks = useCallback((next: Tweaks) => {
    latestTweaks.current = next;
    setTweaksState(next);
  }, []);

  const replaceCreatorData = useCallback((next: Partial<CreatorData>) => {
    latestCreator.current = next;
    setCreatorDataState(next);
  }, []);

  const replaceTweaksAndSaveNow = useCallback(
    async (next: Tweaks) => {
      latestTweaks.current = next;
      setTweaksState(next);
      return persistNow();
    },
    [persistNow],
  );

  // ── Session-only mutators (no DB write) ───────────────────────
  const setTweak = useCallback((key: keyof Tweaks, value: Tweaks[keyof Tweaks]) => {
    const next = { ...latestTweaks.current, [key]: value };
    latestTweaks.current = next;
    setTweaksState(next);
  }, []);

  const getLatestTweaks = useCallback(() => latestTweaks.current, []);

  const setCreatorField = useCallback(<K extends keyof CreatorData>(field: K, value: CreatorData[K]) => {
    const next = { ...latestCreator.current, [field]: value };
    latestCreator.current = next;
    creatorPersistDirtyRef.current = true;
    setCreatorDataState(next);
  }, []);

  // ── uploadImage ───────────────────────────────────────────────
  const uploadImage = useCallback(async (file: File, slot: string): Promise<string | null> => {
    if (!userId?.trim() || readOnly) return null;
    const { url } = await uploadTemplateImage(userId, file, slot);
    return url;
  }, [userId, readOnly]);

  useEffect(() => () => {
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
  }, []);

  const uid = userId?.trim() || '';
  const awaitingFirstSettingsFetch =
    Boolean(uid) && !skipFetch && settingsLoadedForUserRef.current !== uid;
  const effectiveLoading = skipFetch ? false : awaitingFirstSettingsFetch || loading;

  return {
    loading: effectiveLoading,
    saving,
    saveStatus,
    tweaks,
    creatorData,
    setTweak,
    getLatestTweaks,
    setCreatorField,
    uploadImage,
    saveNow,
    setSanitizeTweaksForPersist,
    applyTweaksAndSaveNow,
    replaceTweaks,
    replaceCreatorData,
    replaceTweaksAndSaveNow,
  };
}
